import { useQuery } from "@tanstack/react-query";
import { socialApi } from "./social-api";

export const socialKeys = {
  all: ["social"] as const,
  feed: (targetUserId?: string) =>
    [...socialKeys.all, "feed", targetUserId] as const,
  users: () => [...socialKeys.all, "users"] as const,
  search: (query: string) => [...socialKeys.users(), "search", query] as const,
  friends: () => [...socialKeys.users(), "friends"] as const,
  pendingRequests: () => [...socialKeys.users(), "pending-requests"] as const,
  publicProfile: (handle: string) =>
    [...socialKeys.users(), "profile", handle] as const,
  notifications: () => [...socialKeys.all, "notifications"] as const,
  unreadCount: () => [...socialKeys.notifications(), "unread-count"] as const,
  climbingStats: (userId?: string) =>
    [...socialKeys.all, "climbing-stats", userId] as const,
  comments: (postId: string) =>
    [...socialKeys.all, "comments", postId] as const,
};

export const useFeed = (targetUserId?: string) => {
  return useQuery({
    queryKey: socialKeys.feed(targetUserId),
    queryFn: () => socialApi.getFeed(20, 0, targetUserId),
  });
};

export const useComments = (postId: string, enabled = false) => {
  return useQuery({
    queryKey: socialKeys.comments(postId),
    queryFn: () => socialApi.getComments(postId),
    enabled,
  });
};

export const useSearchUsers = (query: string) => {
  return useQuery({
    queryKey: socialKeys.search(query),
    queryFn: () => socialApi.searchUsers(query),
    enabled: query.length >= 2, // Only run if query is at least 2 chars
  });
};

export const useFriends = () => {
  return useQuery({
    queryKey: socialKeys.friends(),
    queryFn: () => socialApi.getFriends(),
  });
};

export const usePendingRequests = () => {
  return useQuery({
    queryKey: socialKeys.pendingRequests(),
    queryFn: () => socialApi.getPendingRequests(),
  });
};

export const usePublicProfile = (handle: string) => {
  return useQuery({
    queryKey: socialKeys.publicProfile(handle),
    queryFn: () => socialApi.getPublicProfile(handle),
    enabled: !!handle,
  });
};

export const useNotifications = () => {
  return useQuery({
    queryKey: socialKeys.notifications(),
    queryFn: () => socialApi.getNotifications(),
  });
};

export const useUnreadNotificationCount = () => {
  return useQuery({
    queryKey: socialKeys.unreadCount(),
    queryFn: () => socialApi.getUnreadNotificationCount(),
    refetchInterval: 60_000, // Poll every 60 seconds
  });
};

export const useClimbingStats = (userId?: string) => {
  return useQuery({
    queryKey: socialKeys.climbingStats(userId),
    queryFn: () => socialApi.getClimbingStats(userId),
  });
};
